import {
  auditMentionsAuditMentionsMatchesGet,
  auditMentionsAuditMentionsMatchesGetQueryKey,
} from '@mi-front/api-v2';
import { AuditLiveMentionConfidence } from '@mi-front/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { apiClient } from '@app/api';
import { AuditLiveMention } from '../types';

/**
 * Loads a live mention match (api-v2 GET) and mutates confidence.
 * Confidence update still uses the legacy logger-api until an api-v2 write endpoint exists.
 */
export function useAuditMention(stationId: string, id: string) {
  const queryClient = useQueryClient();

  const [rejected, setRejected] = useState(false);

  const getParams = { station: stationId };

  const result = useQuery({
    queryKey: auditMentionsAuditMentionsMatchesGetQueryKey(id, getParams),
    queryFn: async (): Promise<AuditLiveMention> => {
      const data = await auditMentionsAuditMentionsMatchesGet(id, getParams);
      return data as AuditLiveMention;
    },
    enabled: !rejected && !!id,
  });

  const confidenceMutation = useMutation({
    mutationFn: (confidence: AuditLiveMentionConfidence) => {
      return apiClient.audit.updateLiveMentionMatch({
        stationId,
        id,
        data: {
          confidence,
        },
      });
    },
    onMutate: async (confidence) => {
      queryClient.setQueryData(
        auditMentionsAuditMentionsMatchesGetQueryKey(id, getParams),
        (old: Record<string, unknown> | undefined) => ({
          ...old,
          confidence,
        }),
      );

      if (confidence === AuditLiveMentionConfidence.REJECTED) {
        setRejected(true);

        queryClient.setQueryData<AuditLiveMention[]>(['mentions-active'], (current) => {
          return current?.filter((m) => m.id !== id) || [];
        });
      }
    },
  });

  return {
    ...result,
    confidenceMutation,
  };
}

/** @deprecated Prefer `useAuditMention` (master naming). */
export const useAuditLiveMention = useAuditMention;
